import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LatestListAction } from '../types/LatestListAction';
import type List from '../types/List';
import type ListItem from '../types/ListItem';
import type ListItems from '../types/ListItems';
import { addToList, removeFromList } from '../utils/listRequests';

const LIST_ITEMS = 'listItems';
const USER_LIST_ITEMS = 'userListItems';

const favoritesListId = 1000;

type ListsAndListItems = {
  lists: List[];
  listItems: ListItems;
};

export type ListsState = {
  listItems: ListItems;
  userListItems: ListItems;
  lists: List[];
  latestListAction?: LatestListAction;
  belongsToCurrentUser: boolean;
};

const initialState: ListsState = {
  listItems: {},
  lists: [],
  belongsToCurrentUser: false,
  userListItems: {}
};

export const addItemToList = createAsyncThunk(
  'lists/addItemToList',
  (
    {
      listItem,
      belongsToCurrentUser
    }: {
      listItem: ListItem;
      belongsToCurrentUser: boolean;
    },
    { dispatch }
  ) => {
    if (belongsToCurrentUser) {
      dispatch(addItemToUserListItems({ listItem, action: 'add' }));
    }

    dispatch(addItemToListItems({ listItem }));
  }
);

export const removeItemFromList = createAsyncThunk(
  'lists/removeItemFromList',
  ({ listItem, belongsToCurrentUser }: { listItem: ListItem; belongsToCurrentUser: boolean }, { dispatch }) => {
    if (belongsToCurrentUser) {
      dispatch(removeItemFromUserListItems({ listItem, action: 'remove' }));
    }

    dispatch(removeItemFromListItems({ listItem }));
  }
);

export const addListItem = createAsyncThunk(
  'lists/addListItem',
  async (
    {
      userId,
      type,
      objectId,
      listId,
      belongsToCurrentUser
    }: Pick<ListItem, 'listId' | 'objectId' | 'type'> & {
      userId: number;
      belongsToCurrentUser: boolean;
    },
    { dispatch }
  ) => {
    // Call addItemToList to provide immediate feedback to the user
    dispatch(addItemToList({ listItem: { listId, type, objectId }, belongsToCurrentUser }));
    try {
      const { listItems, errors } = await addToList(listId, type, objectId, userId);
      if (listItems && listItems.length > 0) {
        // Call addItemToList again because the response contains a critical "id" field that we don't have prior
        dispatch(addItemToList({ listItem: listItems[0], belongsToCurrentUser }));
      }
      if (errors && errors.length > 0) {
        dispatch(removeItemFromList({ listItem: { listId, type, objectId }, belongsToCurrentUser }));
        console.error('THUNK ACTION addListItem error', errors[0].message);
      }
    } catch (err) {
      dispatch(removeItemFromList({ listItem: { listId, type, objectId }, belongsToCurrentUser }));
      console.error('THUNK ACTION addListItem error', err);
    }
  }
);

export const removeFromListItem = createAsyncThunk(
  'lists/removeFromListItem',
  async (
    {
      userId,
      type,
      itemId,
      listId,
      objectId,
      order,
      belongsToCurrentUser
    }: Pick<ListItem, 'listId' | 'objectId' | 'type' | 'order'> & {
      userId: number;
      itemId: number;
      belongsToCurrentUser: boolean;
    },
    { dispatch }
  ) => {
    dispatch(removeItemFromList({ listItem: { type, listId, objectId }, belongsToCurrentUser }));
    try {
      const { errors } = await removeFromList(listId, itemId, userId);
      if (errors && errors.length > 0) {
        dispatch(addItemToList({ listItem: { listId, type, id: itemId, objectId, order }, belongsToCurrentUser }));
        console.error('THUNK ACTION removeFromListItem error', errors[0].message);
      }
    } catch (err) {
      dispatch(addItemToList({ listItem: { listId, type, id: itemId, objectId, order }, belongsToCurrentUser }));
      console.error('THUNK ACTION removeFromListItem error', err);
    }
  }
);

export const toggleListItem = createAsyncThunk(
  'lists/toggleListItem',
  async ({ userId, listId, type, objectId }: Pick<ListItem, 'listId' | 'objectId' | 'type'> & { userId: number }, { dispatch, getState }) => {
    const {
      lists: { listItems, belongsToCurrentUser }
    } = getState() as { lists: ListsState };

    const item = listItems && listItems[listId] && listItems[listId][type] && listItems[listId][type][objectId];
    if (item) {
      dispatch(
        removeFromListItem({
          userId,
          type,
          itemId: item.id,
          listId,
          objectId,
          order: item.order,
          belongsToCurrentUser
        })
      );
    } else {
      dispatch(addListItem({ userId, type, objectId, listId, belongsToCurrentUser }));
    }
  }
);

const handleAddToListExistingType = (draftState: ListsState, { listId, type, id, order, objectId }: ListItem, listItemsType = LIST_ITEMS) => {
  draftState[listItemsType][listId][type][objectId] = {
    id,
    order
  };
};

const handleAddToListNewType = (draftState: ListsState, { listId, type, id, order, objectId }: ListItem, listItemsType = LIST_ITEMS) => {
  draftState[listItemsType][listId][type] = {
    [objectId]: {
      id,
      order
    }
  };
};

const handleRemoveFromList = (draftState: ListsState, { listItem, action }: { listItem: ListItem; action: string }, listItemsType = LIST_ITEMS) => {
  const list = draftState[listItemsType][listItem.listId];
  delete list[listItem.type][listItem.objectId];

  if (Object.keys(list[listItem.type]).length === 0) {
    delete list[listItem.type];
  }

  if (action) {
    draftState.latestListAction = { listItem, action };
  }
};

const handleAddToList = (draftState: ListsState, { listItem, action }: { listItem: ListItem; action: string }, listItemsType = LIST_ITEMS) => {
  if (Object.prototype.hasOwnProperty.call(draftState[listItemsType][listItem.listId], listItem.type)) {
    handleAddToListExistingType(draftState, listItem, listItemsType);
  } else {
    handleAddToListNewType(draftState, listItem, listItemsType);
  }

  if (action) {
    draftState.latestListAction = { listItem, action };
  }
};

export const listsSlice = createSlice({
  name: 'lists',
  initialState,
  reducers: {
    addItemToListItems: (state, action) => handleAddToList(state, action.payload),
    addItemToUserListItems: (state, action) => handleAddToList(state, action.payload, USER_LIST_ITEMS),
    removeItemFromListItems: (state, action) => handleRemoveFromList(state, action.payload),
    removeItemFromUserListItems: (state, action) => handleRemoveFromList(state, action.payload, USER_LIST_ITEMS),
    addCreatedList: (state, action: PayloadAction<List>) => {
      const favorites = state.lists.find(list => list.id === favoritesListId);
      if (favorites) {
        const favoritesIndex = state.lists.indexOf(favorites);
        state.lists.splice(favoritesIndex + 1, 0, action.payload);
      } else {
        state.lists.unshift(action.payload);
      }

      state.listItems[action.payload.id] = {};
    },
    setListsAndListItems: (state, action: PayloadAction<ListsAndListItems>) => {
      const { lists, listItems } = action.payload;
      state.lists = lists;
      state.listItems = listItems;
    }
  }
});

export const {
  addItemToListItems,
  addItemToUserListItems,
  removeItemFromListItems,
  removeItemFromUserListItems,
  addCreatedList,
  setListsAndListItems
} = listsSlice.actions;
export default listsSlice.reducer;
